import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress
} from '@mui/material';
import { Search, Add, Edit, Delete, Visibility, FilterList, PictureAsPdf } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { studentService, Student, StudentFilters, PaginatedResponse } from '../services/studentService';
import assessmentService from '../services/assessmentService';
import { toast } from 'react-toastify';
import { EnhancedAddUserDialog } from '../components/EnhancedAddUserDialog';
import userService from '../services/userService';

const StudentsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // State
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<StudentFilters>({
    page: 0,
    size: 10,
    search: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0
  });
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddStudentDialog, setShowAddStudentDialog] = useState(false);
  const [universities, setUniversities] = useState<any[]>([]);
  const [providers, setProviders] = useState<any[]>([]);

  // Load students
  const loadStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<Student> = await studentService.getStudents(filters);
      setStudents(response.content);
      setPagination({
        totalElements: response.totalElements,
        totalPages: response.totalPages,
        currentPage: response.page
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, [filters]);

  // Event handlers
  const handleLogout = () => {
    logout();
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      search: event.target.value,
      page: 0 // Reset to first page
    }));
  };

  const handleStatusChange = (event: any) => {
    setFilters(prev => ({
      ...prev,
      status: event.target.value,
      page: 0
    }));
  };

  const handlePageChange = (event: any, newPage: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPage - 1 // MUI Pagination is 1-based, API is 0-based
    }));
  };

  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
    setShowDetailsDialog(true);
  };

  const handleEditStudent = (student: Student) => {
    navigate(`/students/${student.id}/edit`);
  };

  const handleDeleteStudent = (student: Student) => {
    setSelectedStudent(student);
    setShowDeleteDialog(true);
  };

  const confirmDeleteStudent = async () => {
    if (!selectedStudent) return;

    try {
      setLoading(true);
      await studentService.deleteStudent(selectedStudent.id);
      setShowDeleteDialog(false);
      setSelectedStudent(null);
      loadStudents(); // Reload the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete student');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStudent = async () => {
    try {
      // Load universities and providers for the dialog
      const [universitiesData, providersData] = await Promise.all([
        userService.getUniversities(),
        userService.getProviders()
      ]);
      setUniversities(universitiesData);
      setProviders(providersData);
      setShowAddStudentDialog(true);
    } catch (error) {
      console.error('Error loading data for create student:', error);
      toast.error('Failed to load data for creating student');
    }
  };

  const handleAddStudentClose = () => {
    setShowAddStudentDialog(false);
  };

  const handleAddStudentSuccess = () => {
    setShowAddStudentDialog(false);
    loadStudents(); // Reload the students list
    toast.success('Student created successfully');
  };

  const handleDownloadComprehensivePdf = async (student: Student) => {
    try {
      await assessmentService.downloadComprehensivePdf(student.id);
      toast.success(`Downloaded comprehensive report for ${student.firstName} ${student.lastName}`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF report');
    }
  };

  // Utility functions
  const getRiskLevelColor = (riskLevel?: string) => {
    switch (riskLevel) {
      case 'RED': return 'error';
      case 'YELLOW': return 'warning';
      case 'GREEN': return 'success';
      default: return 'default';
    }
  };

  const canManageStudents = user?.userType === 'admin' || user?.userType === 'provider';

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Student Management
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Students
            </Typography>
            <Button onClick={handleBack} sx={{ mb: 2 }}>
              ← Back to Dashboard
            </Button>
          </Box>
          {canManageStudents && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateStudent}
            >
              Add Student
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search students..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    label="Status"
                    onChange={handleStatusChange}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="body2" color="textSecondary">
                  Total: {pagination.totalElements} students
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Students Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Risk Level</TableCell>
                  <TableCell>Assessments</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No students found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            {student.firstName.charAt(0)}
                          </Avatar>
                          {student.firstName} {student.lastName}
                        </Box>
                      </TableCell>
                      <TableCell>{student.email}</TableCell>
                      <TableCell>{student.studyProgram || 'N/A'}</TableCell>
                      <TableCell>{student.providerName || 'Unassigned'}</TableCell>
                      <TableCell>
                        <Chip
                          label={student.lastRiskLevel || 'Not Assessed'}
                          color={getRiskLevelColor(student.lastRiskLevel) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {student.completedAssessments}/{student.totalAssessments}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={studentService.getCompletionPercentage(student)}
                            sx={{ mt: 0.5, height: 4 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {studentService.formatDate(student.lastAssessmentDate)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewStudent(student)}
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadComprehensivePdf(student)}
                          title="Download Comprehensive Report PDF"
                          color="primary"
                        >
                          <PictureAsPdf />
                        </IconButton>
                        {canManageStudents && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEditStudent(student)}
                              title="Edit Student"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteStudent(student)}
                              title="Delete Student"
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Card>
      </Container>

      {/* Student Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Student Details</DialogTitle>
        <DialogContent>
          {selectedStudent && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Name:</Typography>
                <Typography variant="body1">
                  {selectedStudent.firstName} {selectedStudent.lastName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Email:</Typography>
                <Typography variant="body1">{selectedStudent.email}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Phone:</Typography>
                <Typography variant="body1">{selectedStudent.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Study Program:</Typography>
                <Typography variant="body1">{selectedStudent.studyProgram || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Provider:</Typography>
                <Typography variant="body1">{selectedStudent.providerName || 'Unassigned'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Risk Level:</Typography>
                <Chip
                  label={selectedStudent.lastRiskLevel || 'Not Assessed'}
                  color={getRiskLevelColor(selectedStudent.lastRiskLevel) as any}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Assessment Progress:</Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Completed: {selectedStudent.completedAssessments}/{selectedStudent.totalAssessments}
                    ({studentService.getCompletionPercentage(selectedStudent)}%)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={studentService.getCompletionPercentage(selectedStudent)}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
          {canManageStudents && selectedStudent && (
            <Button
              variant="contained"
              onClick={() => {
                setShowDetailsDialog(false);
                handleEditStudent(selectedStudent);
              }}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the student "{selectedStudent?.firstName} {selectedStudent?.lastName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteStudent}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Student Dialog */}
      <EnhancedAddUserDialog
        open={showAddStudentDialog}
        onClose={handleAddStudentClose}
        onSuccess={handleAddStudentSuccess}
        userUniversityId={user?.universityId}
        userType="student"
        universities={universities}
        providers={providers}
      />
    </>
  );
};

export default StudentsPage;